import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import axios from "axios";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeviceStatusCard from '../components/DeviceStatusCard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';

const Shifts = () => {
  const [shifts, setShifts] = useState([]);
  const [newShift, setNewShift] = useState({ shift_name: "", start_time: "", end_time: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editMode, setEditMode] = useState(false);
  const [editShift, setEditShift] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  useEffect(() => {
    fetchShifts();
  }, []);

  const fetchShifts = async () => {
    try {
      const res = await axios.get('https://pdks.bytebridge.com.tr/api/shifts');
      setShifts(res.data);
    } catch (err) {
      setSnackbar({ open: true, message: 'Vardiyalar alınamadı', severity: 'error' });
    }
  };

  const handleChange = (e) => {
    setNewShift({ ...newShift, [e.target.name]: e.target.value });
  };

  const handleAddShift = async () => {
    if (!newShift.shift_name || !newShift.start_time || !newShift.end_time) return;
    try {
      await axios.post('https://pdks.bytebridge.com.tr/api/shifts', newShift);
      setSnackbar({ open: true, message: 'Vardiya eklendi', severity: 'success' });
      setNewShift({ shift_name: "", start_time: "", end_time: "" });
      fetchShifts();
    } catch (err) {
      setSnackbar({ open: true, message: 'Vardiya eklenemedi', severity: 'error' });
    }
  };

  const handleEditClick = (shift) => {
    setEditMode(true);
    setEditShift(shift);
    setNewShift({
      shift_name: shift.ShiftName,
      start_time: shift.StartTime,
      end_time: shift.EndTime,
    });
  };

  const handleUpdateShift = async () => {
    if (!editShift) return;
    try {
      await axios.put(`https://pdks.bytebridge.com.tr/api/shifts/${editShift.ID}`, newShift);
      setSnackbar({ open: true, message: 'Vardiya güncellendi', severity: 'success' });
      setEditMode(false);
      setEditShift(null);
      setNewShift({ shift_name: '', start_time: '', end_time: '' });
      fetchShifts();
    } catch (err) {
      setSnackbar({ open: true, message: 'Vardiya güncellenemedi', severity: 'error' });
    }
  };

  const handleDeleteShift = async (id) => {
    setDeleteTargetId(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await axios.delete(`https://pdks.bytebridge.com.tr/api/shifts/${deleteTargetId}`);
      setSnackbar({ open: true, message: 'Vardiya silindi', severity: 'success' });
      fetchShifts();
    } catch (err) {
      setSnackbar({ open: true, message: 'Vardiya silinemedi', severity: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setDeleteTargetId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteTargetId(null);
  };

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  return (
    <Box p={3}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <Tooltip title="Vardiya işlemlerini bu sayfadan yönetebilirsiniz.">
          <InfoOutlinedIcon color="primary" style={{ marginRight: 8, cursor: 'pointer' }} />
        </Tooltip>
        <Typography variant="h5" gutterBottom>
          Vardiya Tanımları
        </Typography>
      </div>
      {/* Donanım cihazı aktiflik kartı */}
     
      <Box display="flex" gap={2} mb={2}>
        <TextField
          label="Vardiya Adı"
          name="shift_name"
          value={newShift.shift_name}
          onChange={handleChange}
        />
        <TextField
          label="Başlangıç Saati"
          name="start_time"
          type="time"
          value={newShift.start_time}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Bitiş Saati"
          name="end_time"
          type="time"
          value={newShift.end_time}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="contained" onClick={editMode ? handleUpdateShift : handleAddShift} color={editMode ? 'warning' : 'primary'}>
          {editMode ? 'Güncelle' : 'Ekle'}
        </Button>
        {editMode && (
          <Button variant="outlined" color="secondary" onClick={() => { setEditMode(false); setEditShift(null); setNewShift({ shift_name: '', start_time: '', end_time: '' }); }}>
            İptal
          </Button>
        )}
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Vardiya Adı</TableCell>
              <TableCell>Başlangıç</TableCell>
              <TableCell>Bitiş</TableCell>
              <TableCell align="right">İşlem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shifts.map((shift) => (
              <TableRow key={shift.ID}>
                <TableCell>{shift.ShiftName}</TableCell>
                <TableCell>{shift.StartTime}</TableCell>
                <TableCell>{shift.EndTime}</TableCell>
                <TableCell align="right">
                  <Button size="small" color="primary" onClick={() => handleEditClick(shift)}><EditIcon fontSize="small" /></Button>
                  <Button size="small" color="error" onClick={() => handleDeleteShift(shift.ID)}><DeleteIcon fontSize="small" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Vardiya Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>Bu vardiyayı silmek istediğinize emin misiniz?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>İptal</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Sil</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Shifts;
